import { AppBar, Button, Dialog, FormControl, Grid, IconButton, Paper, TextField, Toolbar, Typography, CircularProgress, createTheme, ThemeProvider, Box, } from "@mui/material";
import React, { useState, useEffect, useReducer, useMemo } from "react";
import { useStyles } from "../../common/appStyles";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import CloseIcon from '@mui/icons-material/Close';
import { DecisionEnum, RoleEnum } from "../../types/enums";
import { showSnackbar } from "../../services/snackbar.service";
import { IBasicUserDashboardInfo, IUserCompetencySubmissionInformation, IYouthDashboardInfo } from "../../types/youths";
import { AssignJudgeToSubmission, GetBadge, GetSubmission, GetSubmissionById, JudgeSubmission, SetReviewStartDate, submitFile } from "../../services/user-service";
import DragDropFileUpload from "../../components/HelperComponents/DragDropFileUpload";
import { JudgeDialog } from "../Admin/JudgeDialog";
import { ConfirmBox } from "../../components/HelperComponents/ConfirmBox";
import { ICaseDetailInformation, IJudgeAssignmentRequest, IJudgeSubmissionRequest } from "../../types/judge";
import { BannerAd } from "../../components/main-layout/BannerAd";
const initialState = {
    userId: 0,
    firstName: '',
    lastName: '',
    schoolId: null,
    school: '',
    regionId: null,
    region: '',
    competencyId: null,
    competency: '',
    submissionStage: '',
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        default:
            throw new Error();
    }
}

/**
 * @remarks this page shows a selected submission. It will have the competency and stage for the
 * selected submission as well as the region, school, and name of the student it belongs too. {@link IYouthDashboardInfo}
 * depending on the submission stage the page will either be in a view, update, or create mode. This hides or shows certain
 * page elements based on the logged in user's roleId, as well as the submission stage.
 * @param props - All of the properties and functions that submission 
 * details needs from the page which opened it (`YouthDashboard/CaseDetail`)
 * @returns - an Html component which dynamically hides and shows page elements like badge as well as the judging section
 * of the UI based on the logged in user's Role and the submission's Stage.
*/
export function SubmissionDetail(props) {
    const { showDialog, titleText, selectedUser, selectedSubmission, handleClose } = props;
    const classes = useStyles();
    const [showUserDialog, setShowUserDialog] = useState(false);
    const [showConfirmBox, setShowConfirmBox] = useState(false);
    const [judgeDecision, setJudgeDecision] = useState<DecisionEnum>();
    const [judgeDecision2, setJudgeDecision2] = useState<DecisionEnum>();
    const [File, SetFile] = useState<Blob>();
    const [badge, setBadge] = useState<Blob | MediaSource>();
    const user = useSelector((state: RootState) => state.userDetail);
    const [loadingBadge, setLoadingBadge] = React.useState(false);
    const [isDisabled1, setIsDisabled1] = useState(false);
    const [isDisabled2, setIsDisabled2] = useState(false);
    const [tempSubmissionInfo, setTempSubmissionInfo] = useState<IUserCompetencySubmissionInformation>();
    const [tempUserInfo, setTempUserInfo] = useState<IBasicUserDashboardInfo>();

    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#10AD61'
            },
            body1: {
                color: '#00000'
            }
        },
    });

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        schoolId: selectedUser.schoolId,
        school: selectedUser?.school,
        regionId: selectedUser?.regionId,
        region: selectedUser?.region,
        competencyId: selectedSubmission?.competencyId,
        competency: selectedSubmission?.competency,
        submissionStage: selectedSubmission?.submissionStage,
        title: selectedSubmission?.title,
        isAwarded: selectedSubmission?.isAwarded,
        judgeUserId: selectedSubmission?.judgeUserId,
        judgeFeedback: selectedSubmission?.judgeFeedback,
        judge2UserId: selectedSubmission?.judge2UserId,
        judge2Feedback: selectedSubmission?.judge2Feedback
    });

    const {
        firstName,
        lastName,
        schoolId,
        school,
        regionId,
        region,
        competency,
        competencyId,
        submissionStage,
        title,
        judgeUserId,
        isAwarded,
        judgeFeedback,
        judge2UserId,
        judge2Feedback
    } = state;

    const setField = (field, value) => {
        dispatch({ type: 'SET_FIELD', field, value });
    };

    const handleFileUpload = useMemo(() => {
        return (newFile) => {
            SetFile(newFile);
        };
    }, []);

    //judging form handlers
    const handleUpdate = () => {
        setTempSubmissionInfo(
            {
                competencyId: selectedSubmission.competencyId,
                competency: selectedSubmission.competency,
                title: selectedSubmission.title,
                submissionCreateDateTime: selectedSubmission.submissionCreateDateTime,
                submissionSubmitDateTime: selectedSubmission.submissionSubmitDateTime,
                isAwarded: isAwarded,
                submissionStage: submissionStage,
                submissionDecisionDateTime: selectedSubmission.submissionDecisionDateTime,
                submissionId: selectedSubmission.submissionId,
                judgeUserId: judgeUserId,
                judgeFeedback: judgeFeedback,
                judge2UserId: judge2UserId,
                judge2Feedback: judge2Feedback
            }
        )
        setTempUserInfo(
            {
                userId: selectedUser.userId,
                firstName: firstName,
                lastName: lastName,
                schoolId: schoolId,
                school: school,
                regionId: regionId,
                region: region
            }
        )
        setShowUserDialog(true);
    }

    const handleCloseForm = () => {
        setShowUserDialog(false);
    }

    //confirm box handlers
    const handleConfirm = (event) => {
        setShowConfirmBox(true);
    }

    const handleCloseConfirm = () => {
        setShowConfirmBox(false);
    }

    const loadBadge = async () => {
        if (isAwarded === true && user.roleId === RoleEnum.Youth) {
            setLoadingBadge(true);
            const badgeDetails = await GetBadge(competencyId);
            setBadge(badgeDetails);
        }
        else if (isAwarded === true) {
            setLoadingBadge(true);
            const badgeDetails = await GetBadge(competencyId, selectedUser.userId);
            setBadge(badgeDetails);
        }
        setLoadingBadge(false);
    }

    const refreshSubmission = () => {
        getSubmissionById();
    }

    const getSubmissionById = async () => {
        try {
            const updatedSubmission: ICaseDetailInformation = await GetSubmissionById(selectedSubmission.submissionId);
            setField('firstName', updatedSubmission.firstName);
            setField('lastName', updatedSubmission.lastName);
            setField('schoolId', updatedSubmission.schoolId);
            setField('school', updatedSubmission.school);
            setField('regionId', updatedSubmission.regionId);
            setField('region', updatedSubmission.region);
            setField('competencyId', updatedSubmission.competencyId);
            setField('competency', updatedSubmission.competency);
            setField('submissionStage', updatedSubmission.submissionStage);
            setField('title', updatedSubmission.title);
            setField('isAwarded', updatedSubmission.isAwarded);
            setField('judgeUserId', updatedSubmission.judgeUserId);
            setField('judgeFeedback', updatedSubmission.judgeFeedback);
            setField('judge2UserId', updatedSubmission.judge2UserId);
            setField('judge2Feedback', updatedSubmission.judge2Feedback);

            setTempSubmissionInfo(
                {
                    competencyId: updatedSubmission.competencyId,
                    competency: updatedSubmission.competency,
                    title: updatedSubmission.title,
                    submissionCreateDateTime: updatedSubmission.submissionCreateDateTime,
                    submissionSubmitDateTime: updatedSubmission.submissionSubmitDateTime,
                    isAwarded: updatedSubmission.isAwarded,
                    submissionStage: updatedSubmission.submissionStage,
                    submissionDecisionDateTime: updatedSubmission.submissionDecisionDateTime,
                    submissionId: updatedSubmission.submissionId,
                    judgeUserId: updatedSubmission.judgeUserId,
                    judgeFeedback: updatedSubmission.judgeFeedback,
                    judge2UserId: updatedSubmission.judge2UserId,
                    judge2Feedback: updatedSubmission.judge2Feedback
                }
            );

        } catch (error) {
            showSnackbar("error updating submission Info", 'error', 5000);
        }
    }

    const handleSubmit = async () => {
        setShowConfirmBox(false);
        try {
            const formData = new FormData();
            if (File) {
                formData.append('SubmissionFile', File);
                formData.append('Title', title);
                formData.append('IsDraft', 'false');
                formData.append('CompetencyId', competencyId);
            } else {
                console.error('selectedFile is null or undefined.');
            }
            await submitFile(formData);
            showSnackbar('Successfully Submitted!', 'success', 5000);
            handleClose();
        }
        catch (error: any) {
            showSnackbar(error, 'error', 5000);
        }
    }

    const getSubmission = async (competencyId: number, userId?: number) => {
        if (submissionStage !== 'Not Started') {
            const submission = await GetSubmission(competencyId, userId);
            SetFile(submission);
        }
    }

    const handleJudgeAssignmentChange = async (judgeId) => {
        try {
            const request: IJudgeAssignmentRequest = {
                submissionId: selectedSubmission.submissionId,
                judgeUserId: judgeId
            };
            await AssignJudgeToSubmission(request);
            showSnackbar("Judge Assigned Successfully", "success", 3000);
            refreshSubmission();
        } catch (error) {
            showSnackbar("error assigning Judge", 'error', 3000);
        }
    }

    const handleJudgeSubmission = async (feedback: string, decision: number) => {
        try {
            const request: IJudgeSubmissionRequest = {
                submissionId: selectedSubmission.submissionId,
                feedback: feedback,
                isAwarded: decision === 2 ? true : false
            };
            await JudgeSubmission(request);
            showSnackbar("Decision Submitted Successfully", "success", 3000);
            handleCloseForm();
            refreshSubmission();
            if (decision === 2) {
                setField('isAwarded', true);
                loadBadge();
            }
        } catch (error) {
            showSnackbar("error Judging Submission", 'error', 3000);
        }
    }

    const handleSaveDraft = async () => {
        try {
            const formData = new FormData();
            if (File) {
                formData.append('SubmissionFile', File);
                formData.append('Title', title);
                formData.append('IsDraft', 'true');
                formData.append('CompetencyId', competencyId);
            } else {
                console.error('selectedFile is null or undefined.');
            }
            await submitFile(formData);
            showSnackbar(' Draft Saved Successfully', 'success', 5000);
        }
        catch (error) {
            showSnackbar('Error saving draft', 'error', 5000);
        }
    }

    const judgeButtonText = () => {
        if (user.roleId === RoleEnum.Judge) {
            if (judgeUserId !== user.id) {
                return "View Form";
            }
            else if (selectedSubmission.submissionStage === "Submission Approved" ||
                selectedSubmission.submissionStage === "Submission Denied by Both Judges" ||
                selectedSubmission.submissionStage === "Submission Denied by Judge 1—Awaiting Second Judge''s Review" ||
                selectedSubmission.submissionStage === "Submission Denied by Judge 1—Awaiting Second Judge Assignment"
            ) {
                return "View Form";
            }
            else {
                return "Update Form";
            }
        }
        else if (user.roleId < RoleEnum.Judge) {
            if (selectedSubmission.submissionStage === "Submission Approved" ||
                selectedSubmission.submissionStage === "Submission Denied by Both Judges"
            ) {
                return "View Form";
            }
            else {
                return "Update Form";
            }
        }
        else {
            return "View Form";
        }
    }

    const downloadBadge = () => {
        if (badge && user.roleId === 6) {
            const url = URL.createObjectURL(badge);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = competency as string;
            anchor.click();
            URL.revokeObjectURL(url);
        }
    }

    const startNew = () => {
        setField('submissionStage', "Not Started")
        setField('title', "");
        SetFile(undefined);
    }

    useEffect(() => {
        switch (submissionStage) {
            case 'Submission Denied by Judge 1—Awaiting Second Judge Assignment':
                setJudgeDecision(3);
                setIsDisabled1(true);
                break;
            case "Submission Denied by Judge 1—Awaiting Second Judge's Review":
                setJudgeDecision(3);
                setJudgeDecision2(1);
                setIsDisabled1(true);
                break;
            case 'Submission Denied by Both Judges':
                setJudgeDecision(3);
                setJudgeDecision2(3);
                setIsDisabled1(true);
                setIsDisabled2(true);
                break;
            case 'Submission Approved':
                if (judge2UserId !== null) {
                    setJudgeDecision(3);
                    setJudgeDecision2(2);
                }
                else {
                    setJudgeDecision(2);
                }
                setIsDisabled1(true);
                setIsDisabled2(true);
                break;
            default:
                setJudgeDecision(1);
        }
        if (isAwarded === true) {
            loadBadge();
        }
    }, [submissionStage]);

    const updateReviewDate = async (submissionId: number) => {
        await SetReviewStartDate(submissionId);
    }

    useEffect(() => {
        if (user.id === selectedSubmission.judge2UserId || user.id === selectedSubmission.judgeUserId) {
            updateReviewDate(selectedSubmission.submissionId);
        }
        if (selectedUser.userId !== user.id) {
            getSubmission(selectedSubmission.competencyId, selectedUser.userId)
        }
        else {
            getSubmission(selectedSubmission.competencyId)
        }
        if (title === '_') { setField('title', ""); }
    }, [selectedSubmission, selectedUser]);

    return (
        <div>
            <ThemeProvider theme={theme}>
                <Dialog
                    open={showDialog}
                    onClose={handleClose}
                    maxWidth={"lg"}
                    fullScreen
                >
                    <AppBar sx={{ position: 'fixed' }}>
                        <Toolbar>
                            <Typography variant="h6" component="div" style={{ flex: 1 }}>
                                {titleText}
                            </Typography>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleClose}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    <Grid className={classes.wrapper} alignItems={"flex-start"} justifyContent={"flex-start"}>
                        <Grid
                            container
                            direction={"column"}
                            justifyContent={'flex-start'}
                            alignItems={"center"}
                            className={classes.subWrapper2}
                        >
                            {user.roleId >= RoleEnum.CredentialingManager && <BannerAd />}
                            <Grid container direction={"row"} alignItems={"flex-start"} justifyContent={"flex-start"}>
                                <Grid item xs={4}>
                                    <Grid
                                        container
                                        className={classes.gridPadding}
                                        direction={"column"}
                                        justifyContent={"space-evenly"}
                                        rowSpacing={2}
                                    >
                                        <Grid item xs={3} md={3} className={classes.gridHeight}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {competency}: {submissionStage}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3} md={3} className={classes.gridHeight}>
                                            <Typography variant="body1">
                                                Region: {region} <br />
                                                School: {school}
                                            </Typography>
                                        </Grid>
                                        {user.id === selectedUser.id || <Grid item xs={3} md={3} className={classes.gridHeight}>
                                            <Typography variant="body1">
                                                {firstName} {lastName}
                                            </Typography>
                                        </Grid>}
                                        <Grid item xs={3} md={3} className={classes.gridHeight}>
                                            <FormControl fullWidth >
                                                <TextField
                                                    required
                                                    disabled={
                                                        (submissionStage !== 'Not Started' && submissionStage !== 'Draft In-Progress') ||
                                                            !(user.roleId >= RoleEnum.Youth || user.roleId <= RoleEnum.CredentialingManager) ? true : false
                                                    }
                                                    label="Title"
                                                    value={title}
                                                    onChange={event => setField('title', event.target.value)}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4}>
                                    <Grid
                                        container
                                        className={classes.gridPadding}
                                        direction={"column"}
                                        justifyContent={"space-evenly"}
                                    >
                                        <Grid item xs={12} className={classes.gridHeight}>
                                            <div style={{ padding: "4.5vh" }}>
                                                <DragDropFileUpload
                                                    onFileUpload={handleFileUpload}
                                                    submissionStage={submissionStage}
                                                    userRole={user.roleId}
                                                    myFile={File}
                                                    title={title}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={3}>
                                    <Grid
                                        container
                                        className={classes.gridPadding}
                                        direction={"column"}
                                        justifyContent={"center"}
                                        alignItems={"center"}
                                    >
                                        {(isAwarded) && <Grid container direction={"row"} alignItems={"center"} justifyContent={"center"}>
                                            <Grid item xs={12} className={classes.gridHeight}>
                                                {loadingBadge ?
                                                    <Grid>
                                                        <Grid container style={{ minHeight: '46vh' }} alignItems={'center'} justifyContent={'center'}>
                                                            <Grid item>
                                                                <CircularProgress style={{ minHeight: '5vh', minWidth: '5vh' }} />
                                                            </Grid>
                                                        </Grid>

                                                    </Grid>
                                                    :
                                                    <Grid container alignItems={"center"} justifyContent={"center"} direction={"column"}>
                                                        {user.roleId === RoleEnum.Youth && <Grid item>
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Click the badge to download.</Typography>
                                                        </Grid>}
                                                        <Grid item>
                                                            <div>
                                                                <img
                                                                    id="badge"
                                                                    className={user.roleId === RoleEnum.Youth ? classes.hoverClickable : classes.hoverNotClickable}
                                                                    src={badge && URL.createObjectURL(badge)}
                                                                    onClick={downloadBadge}
                                                                    style={{ maxWidth: "45vh", maxHeight: "45vh" }} />
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                }
                                            </Grid>

                                        </Grid>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={1.2} alignSelf={"center"} sx={{ minWidth: "100vh" }} >
                                <Grid container alignItems={"center"} justifyContent={"center"}>
                                    {submissionStage === 'Not Started' || submissionStage === 'Draft In-Progress' ?
                                        (user.roleId === RoleEnum.Youth &&
                                            <Grid item xs={6}>
                                                <Grid container direction={"row"} columnSpacing={3}>
                                                    <Grid item xs={6} md={6}>
                                                        <Button
                                                            onClick={handleSaveDraft}
                                                            variant="outlined"
                                                            disabled={!File || title === ""}
                                                            fullWidth>
                                                            save as draft
                                                        </Button>
                                                    </Grid>
                                                    <Grid item xs={6} md={6}>
                                                        <Button
                                                            onClick={handleConfirm}
                                                            variant="contained"
                                                            disabled={!File || title === ""}
                                                            fullWidth>
                                                            Submit
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        )
                                        :
                                        <Grid item xs={11}>
                                            {(user.roleId < RoleEnum.SchoolCoordinator ||
                                                submissionStage === 'Submission Approved' ||
                                                submissionStage === 'Submission Denied by Both Judges') &&
                                                <Paper variant={"outlined"} style={{ border: '1px solid #000' }}>
                                                    <Grid container justifyContent={judge2UserId ? "space-between" : "flex-start"} direction={"row"} sx={{ padding: "5vh" }} >
                                                        {(submissionStage === 'Awaiting Judge Assignment') ?
                                                            <Grid item xs={4}>
                                                                <Typography variant="button">
                                                                    Judge: to be assigned
                                                                </Typography>
                                                            </Grid>
                                                            :
                                                            <Grid item xs={4}>
                                                                <Grid container direction={"column"} alignItems={"flex-start"} justifyContent={"center"} rowSpacing={3}>
                                                                    <Grid item>
                                                                        <Typography variant="body1">
                                                                            <Box fontWeight="bold" component="span">
                                                                                First Judge Feedback:
                                                                            </Box>
                                                                            {judgeFeedback}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item>
                                                                        <Typography variant="button">
                                                                            Judge Decision: {judgeDecision === DecisionEnum.Pending ? 'Pending' : (judgeDecision === DecisionEnum.Approved ? 'Approved' : 'Denied')}
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>}
                                                        {user.roleId <= RoleEnum.SchoolCoordinator ?
                                                            <Grid item xs={4} minHeight={'100%'} alignSelf={"flex-end"}>
                                                                <Grid container justifyContent={"center"} alignItems={"flex-end"}>
                                                                    <Grid item>
                                                                        <Button variant="contained" onClick={handleUpdate}>
                                                                            {judgeButtonText()}
                                                                        </Button>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                            :
                                                            (submissionStage === 'Submission Denied by Both Judges' &&
                                                                <Grid item xs={4} minHeight={'100%'} alignSelf={"flex-end"}>
                                                                    <Grid container justifyContent={"center"} alignItems={"flex-end"}>
                                                                        <Grid item>
                                                                            <Button variant="contained" onClick={startNew}>
                                                                                Start New Submission
                                                                            </Button>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>)
                                                        }
                                                        {(submissionStage === 'Submission Denied by Judge 1—Awaiting Second Judge Assignment' ||
                                                            submissionStage === "Submission Denied by Judge 1—Awaiting Second Judge's Review" ||
                                                            submissionStage === 'Submission Denied by Both Judges' ||
                                                            (submissionStage === 'Submission Approved' && judge2UserId !== null)) &&
                                                            <Grid item xs={4} >
                                                                {judge2UserId ? <Grid container direction={"column"} alignItems={"center"} justifyContent={"center"} rowSpacing={3}>
                                                                    <Grid item>
                                                                        <Typography variant="body1">
                                                                            <Box fontWeight="bold" component="span">
                                                                                Second Judge Feedback:
                                                                            </Box>
                                                                            {judge2Feedback}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item>
                                                                        <Typography variant="button">
                                                                            Judge Decision: {judgeDecision2 === DecisionEnum.Pending ? 'Pending' : (judgeDecision2 === DecisionEnum.Approved ? 'Approved' : 'Denied')}
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid> :
                                                                    <Grid container direction={"column"} alignItems={"flex-start"} justifyContent={"center"} rowSpacing={3}>
                                                                        <Grid item>
                                                                            <Typography variant="body1" fontWeight={"bold"}>
                                                                                Judge to be Assigned
                                                                            </Typography>
                                                                        </Grid>
                                                                    </Grid>
                                                                }
                                                            </Grid>}
                                                    </Grid>
                                                </Paper>}
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item sm={12}>
                            {showUserDialog && <JudgeDialog
                                showDialog={showUserDialog}
                                selectedSubmission={tempSubmissionInfo}
                                selectedUser={tempUserInfo}
                                handleClose={handleCloseForm}
                                changeJudge={handleJudgeAssignmentChange}
                                handleSave={handleJudgeSubmission}
                                judgeDecision={judgeDecision}
                                judgeDecision2={judgeDecision2}
                            />}
                        </Grid>
                        {showConfirmBox && <ConfirmBox
                            showDialog={showConfirmBox}
                            handleClose={handleCloseConfirm}
                            handleSubmit={handleSubmit}
                        />}
                    </Grid >
                </Dialog >
            </ThemeProvider>
        </div >
    );
}
